import * as React from "react";
import Image, { StaticImageData } from "next/legacy/image";
import FacebookIcon from "@mui/icons-material/Facebook";
import { pipe } from "fp-ts/function";
import { option } from "fp-ts";
import {
    AppBar,
    Box,
    Container,
    Grid,
    Hidden,
    IconButton,
    Link,
    Toolbar,
    Typography,
} from "@mui/material";
import { UrlObject } from "url";
import { index, SubdomainQuery } from "../../../routes/org";
import { ExternalLink, InternalLink } from "../../common/Link";
import { LogoXathlo } from "../../LogoXathlo";
import fenixLogo from "../../../../public/feje/fenix.png";
import jesenikLogo from "../../../../public/jesenickysnek/jesenik.svg";
import fitkoLogo from "../../../../public/jesenickysnek/fitko.png";
import jvsLogo from "../../../../public/jesenickysnek//JVS.png";
import wooxLogo from "../../../../public/feje/woox-logo.png";
import olKrajLogo from "../../../../public/feje/olKraj.jpg";
import cusLogo from "../../../../public/feje/cus.png";
import naturfytLogo from "../../../../public/feje/naturfyt-logo.png";
import atexLogo from "../../../../public/feje/logo_atexsport_green.png";
import sport2000Logo from "../../../../public/feje/sport2000.png";

interface Props extends SubdomainQuery {
    children: React.ReactNode;
}

export const FejeLayout = (p: Props) => {
    return (
        <Box
            sx={{
                // backgroundColor: (theme) => theme.palette.grey[200],
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
            }}
        >
            <AppBar
                position="static"
                color="default"
                elevation={4}
                sx={{
                    pt: 1,
                }}
            >
                <Container maxWidth={"lg"}>
                    <Toolbar
                        sx={{
                            flexWrap: "wrap",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box>
                            <InternalLink
                                href={index.route({
                                    subdomain: p.subdomain,
                                })}
                            >
                                <Link>
                                    <img
                                        width={75}
                                        height={75}
                                        alt={"Fenix ski team Jeseník"}
                                        src={"/feje/feje-logo.png"}
                                    />
                                </Link>
                            </InternalLink>
                        </Box>

                        {/*<Grid*/}
                        {/*    container*/}
                        {/*    component={"nav"}*/}
                        {/*    sx={{ width: "auto" }}*/}
                        {/*    spacing={2}*/}
                        {/*>*/}
                        {/*    <NavLink*/}
                        {/*        label={"Archiv výsledků"}*/}
                        {/*        route={calendar.route({*/}
                        {/*            subdomain: p.subdomain,*/}
                        {/*        })}*/}
                        {/*    />*/}
                        {/*</Grid>*/}
                        <Hidden mdDown>
                            <Box>
                                <IconButton
                                    target={"_blank"}
                                    href={"https://www.facebook.com/fenixjes"}
                                >
                                    <FacebookIcon />
                                </IconButton>
                            </Box>
                        </Hidden>
                    </Toolbar>
                </Container>
            </AppBar>
            <Box
                sx={{
                    my: 4,
                }}
            >
                {p.children}
            </Box>
            <Box
                component="footer"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.background.default,
                    marginTop: "auto",
                    p: 2,
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container maxWidth="xl">
                    <Typography variant={"h5"} gutterBottom>
                        Sponzoři:
                    </Typography>
                    <Grid
                        container
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        spacing={2}
                    >
                        <Partner
                            logo={fenixLogo}
                            web={"https://www.fenixgroup.cz/cs"}
                            name={"FENIX Trading s.r.o."}
                            height={100}
                        />
                        <Partner
                            logo={jvsLogo}
                            web={"https://www.voda-jvs.cz/"}
                            name={
                                "Jesenická vodohospodářská společnost , s.r.o."
                            }
                            height={70}
                        />
                        <Partner
                            logo={fitkoLogo}
                            web={"https://www.fitko-jesenik.cz/"}
                            name={"FIT:KO Jeseník"}
                            height={90}
                        />
                        <Partner
                            logo={jesenikLogo}
                            web={"https://www.jesenik.org/"}
                            name={"Město Jeseník"}
                            height={70}
                        />
                        <Partner
                            logo={olKrajLogo}
                            web={"https://www.olkraj.cz/"}
                            name={"Olomoucký kraj"}
                            height={100}
                        />
                        <Partner
                            logo={cusLogo}
                            web={"https://www.cuscz.cz/"}
                            name={"Česká unie sportu"}
                            height={80}
                        />
                        <Partner
                            logo={wooxLogo}
                            web={"https://www.woox.cz/"}
                            name={"WOOX"}
                            height={30}
                        />
                        <Partner
                            logo={naturfytLogo}
                            web={"http://naturfyt.cz/"}
                            name={"Naturfyt – Bio"}
                            height={70}
                        />
                        <Partner
                            logo={atexLogo}
                            web={"https://www.atexsport.cz/"}
                            name={"Atex"}
                            height={50}
                        />
                        <Partner
                            logo={sport2000Logo}
                            web={"https://sport2000online.cz/"}
                            name={"Sport 2000"}
                            height={70}
                        />
                        <PartnerGridItem>
                            <ExternalLink href={"https://xathlo.com"}>
                                <LogoXathlo />
                            </ExternalLink>
                        </PartnerGridItem>
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
};

interface PartnerProps {
    logo: StaticImageData;
    web: string;
    name: string;
    height?: number;
}

interface Rect {
    width: number;
    height: number;
}

const Partner = (p: PartnerProps) => {
    const imageSize: Partial<Rect> = pipe(
        option.fromNullable(p.height),
        option.fold(
            () => p.logo,
            (h) => {
                const w2h = p.logo.width / p.logo.height;
                const width = w2h * h;
                return { width, height: p.height };
            }
        )
    );

    return (
        <PartnerGridItem>
            <ExternalLink href={p.web}>
                <Image
                    src={p.logo}
                    alt={p.name}
                    width={imageSize.width}
                    height={imageSize.height}
                />
            </ExternalLink>
        </PartnerGridItem>
    );
};
interface PartnerGridItemProps {
    children: React.ReactNode;
}
const PartnerGridItem = (p: PartnerGridItemProps) => {
    return (
        <Grid
            item
            xs={12}
            sm={4}
            md={3}
            lg={2}
            flexGrow={1}
            sx={{ textAlign: "center" }}
        >
            {p.children}
        </Grid>
    );
};

interface NavLinkProps {
    label: string;
    route: UrlObject;
}
const NavLink = (p: NavLinkProps) => (
    <NavItem>
        <InternalLink href={p.route}>
            <Link variant="button" underline={"hover"}>
                {p.label}
            </Link>
        </InternalLink>
    </NavItem>
);

interface NavItemProps {
    children: React.ReactNode;
}

const NavItem = (props: NavItemProps) => <Grid item>{props.children}</Grid>;
